import React, { FC, useCallback, useEffect, useRef, useState } from 'react'

import { useQuery } from '@apollo/client'
import { RenderFunctionResult } from 'react-on-rails'

import { GET_CURRENT_CUSTOMER, GET_CURRENT_SHOP_PAYMENT_SETTINGS } from 'Const'
import { Customer, Shop } from 'Types'

import GQLProvider from 'Components/GQLProvider'

import LoginForm from './components/LoginForm'
import { useVisibleProfileTabs } from './hooks/useVisibleProfileTabs'
import { TabNav, ProfileTabKey, slugFromTabKey, tabKeyFromSlug } from './TabNav'
import { AddressesTab } from './tabs/Addresses'
import { GiftCards } from './tabs/GiftCard'
import { HomeTab } from './tabs/Home'
import { OrdersTab } from './tabs/Orders'
import { PaymentTab } from './tabs/Payment'
import { ProfileTab } from './tabs/Profile'
import { ReservationsTab } from './tabs/Reservations'

const TAB_COMPONENTS: Partial<Record<ProfileTabKey, React.FC>> = {
  orders: OrdersTab,
  reservations: ReservationsTab,
  giftCards: GiftCards,
}

const PROFILE_BASE_PATH = '/profile'

const getTabFromLocation = (): ProfileTabKey => {
  const slug = window.location.pathname.replace(/^\/?profile\/?/, '')

  return tabKeyFromSlug(slug)
}

interface Props {
  authenticityToken: string
  customer: Customer
  googlePlacesApiKey: string
  shop: Pick<Shop, 'isSpreedlyPaymentsAvailable' | 'isSquarePaymentsAvailable'>
}

export const Profile: FC<Props> = ({ customer, googlePlacesApiKey, shop }) => {
  const [activeTab, setActiveTab] = useState<ProfileTabKey>(getTabFromLocation)

  const { data: currentCustomerData, refetch: refetchCurrentCustomer } = useQuery(GET_CURRENT_CUSTOMER)
  const currentCustomer = (
    currentCustomerData?.currentCustomer !== undefined ? currentCustomerData.currentCustomer : customer
  ) as Customer

  const visibleTabs = useVisibleProfileTabs(Boolean(currentCustomer?.signedUp))

  // A link into a tab whose feature is switched off falls back to Home.
  const currentTab = visibleTabs.includes(activeTab) ? activeTab : 'home'
  const ActiveTabComponent = TAB_COMPONENTS[currentTab]

  const pageRef = useRef<HTMLDivElement>(null)
  const isFirstTab = useRef(true)

  // Jump back to the top of the profile section on every tab switch — without
  // this, switching tabs while scrolled down leaves the new tab's content
  // rendered off-screen below the fold. Skip on the very first render so
  // loading straight into a deep-linked tab doesn't also scroll the page.
  useEffect(() => {
    if (isFirstTab.current) {
      isFirstTab.current = false
      return
    }
    pageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [currentTab])

  useEffect(() => {
    if (currentTab !== activeTab) window.history.replaceState({ tab: currentTab }, '', PROFILE_BASE_PATH)
  }, [activeTab, currentTab])

  const navigateToTab = useCallback((tab: ProfileTabKey) => {
    setActiveTab(tab)

    const slug = slugFromTabKey(tab)
    const path = slug ? `${PROFILE_BASE_PATH}/${slug}` : PROFILE_BASE_PATH
    if (window.location.pathname !== path) {
      window.history.pushState({ tab }, '', path)
    }
  }, [])

  useEffect(() => {
    const onPopState = () => setActiveTab(getTabFromLocation())
    window.addEventListener('popstate', onPopState)

    return () => window.removeEventListener('popstate', onPopState)
  }, [])

  const { data: currentShopData } = useQuery(GET_CURRENT_SHOP_PAYMENT_SETTINGS)
  const currentShop = currentShopData?.currentShop ?? shop

  const handleLoginComplete = useCallback(() => {
    void refetchCurrentCustomer()
    document.dispatchEvent(new CustomEvent('sign-up', { detail: { source: 'react' } }))
  }, [refetchCurrentCustomer])

  return (
    <div ref={pageRef} className="user-profile-page">
      {currentCustomer?.signedUp ? null : (
        <LoginForm currentCustomer={currentCustomer} onLoginComplete={handleLoginComplete} />
      )}
      {currentCustomer?.signedUp ? (
        <>
          <TabNav activeTab={currentTab} tabs={visibleTabs} variant="chips" onChange={navigateToTab} />
          <div className="user-profile-page__shell">
            <TabNav activeTab={currentTab} tabs={visibleTabs} variant="rail" onChange={navigateToTab} />
            <main className="user-profile-page__content">
              {currentTab === 'home' ? (
                <HomeTab onNavigateTab={navigateToTab} />
              ) : currentTab === 'addresses' ? (
                <AddressesTab customer={currentCustomer} googlePlacesApiKey={googlePlacesApiKey} />
              ) : currentTab === 'profile' ? (
                <ProfileTab customer={currentCustomer} />
              ) : currentTab === 'payment' ? (
                <PaymentTab
                  isSpreedlyGateway={currentShop.isSpreedlyPaymentsAvailable}
                  isSquareGateway={currentShop.isSquarePaymentsAvailable}
                />
              ) : ActiveTabComponent ? (
                <ActiveTabComponent />
              ) : null}
            </main>
          </div>
        </>
      ) : null}
    </div>
  )
}

const Wrapper = (props: Props): RenderFunctionResult => {
  const Wrapped = () => (
    <GQLProvider>
      <Profile {...props} />
    </GQLProvider>
  )

  return Wrapped
}

Wrapper.renderFunction = true

export default Wrapper
